/**
 * Utilitário para determinar o lote ativo e com vagas disponíveis para uma corrida.
 * 
 * Regras:
 * 1. O lote não pode estar desativado manualmente (lote.ativo !== false).
 * 2. O lote deve ter vagas disponíveis (vagas_disponiveis === undefined || vagas_disponiveis === null || vagas_disponiveis > 0)
 *    ou qtd_vagas === 0 (ilimitado) ou qtd_vendidos < qtd_vagas.
 * 3. O lote deve estar dentro do período de vigência (lote.esta_ativo === true ou data_inicio <= hoje <= data_fim).
 * 4. Se houver múltiplos lotes elegíveis, seleciona o de menor ordem (ex: 1º lote antes do 2º lote).
 * 
 * @param {Array} lotes Lista de lotes cadastrados na corrida
 * @returns {Object|null} O lote ativo e disponível, ou null se nenhum estiver disponível
 */
export function obterLoteAtivoEDisponivel(lotes) {
    if (!Array.isArray(lotes) || lotes.length === 0) return null;

    const hoje = new Date();
    const year = hoje.getFullYear();
    const month = String(hoje.getMonth() + 1).padStart(2, '0');
    const day = String(hoje.getDate()).padStart(2, '0');
    const hojeStr = `${year}-${month}-${day}`;

    // Ordenar por ordem crescente (1º lote, 2º lote...)
    const ordenados = [...lotes].sort((a, b) => (a.ordem || 1) - (b.ordem || 1));

    // 1. Busca por lote que atenda: ativo + com vagas + vigente
    const loteValido = ordenados.find(l => {
        // Desativado manualmente?
        if (l.ativo === false) return false;

        // Vagas esgotadas?
        const temVagas = (l.vagas_disponiveis === undefined || l.vagas_disponiveis === null || l.vagas_disponiveis > 0) &&
                         (l.qtd_vagas === 0 || l.qtd_vagas === undefined || l.qtd_vagas === null || (l.qtd_vendidos || 0) < l.qtd_vagas);
        if (!temVagas) return false;

        // Marcado como ativo pelo backend?
        if (l.esta_ativo === true) return true;

        // Validação por datas
        if (l.data_inicio && l.data_fim) {
            const dtInicio = String(l.data_inicio).split('T')[0];
            const dtFim = String(l.data_fim).split('T')[0];
            return dtInicio <= hojeStr && dtFim >= hojeStr;
        }

        return false;
    });

    if (loteValido) return loteValido;

    // Fallback: se nenhum lote tiver data/esta_ativo estrito, mas tiver um lote ativo com vagas
    return ordenados.find(l => {
        if (l.ativo === false) return false;
        const temVagas = (l.vagas_disponiveis === undefined || l.vagas_disponiveis === null || l.vagas_disponiveis > 0);
        return temVagas && l.esta_ativo;
    }) || null;
}

/**
 * Verifica se a faixa etária informada representa 60 anos ou mais (elegível a 50% de desconto).
 * Aceita variações como '60+', '60 +', '+60', '60 ou mais', '60 anos ou mais', 'Acima de 60'.
 * Faixas fechadas como '18-60' ou '18 a 60 anos' NÃO são elegíveis.
 *
 * @param {string} faixaEtaria
 * @returns {boolean}
 */
export function isFaixaEtaria60Mais(faixaEtaria) {
    if (!faixaEtaria || typeof faixaEtaria !== 'string') return false;
    const texto = faixaEtaria.trim().toLowerCase();
    if (!texto) return false;

    // Sem lookbehind (não suportado em Safari < 16.4): usa (^|\D) no lugar de (?<!\d)
    const padroes = [
        /(^|\D)60\s*\+/,
        /\+\s*60(?!\d)/,
        /(^|\D)60\s*(?:anos)?\s*ou\s*mais/,
        /acima\s*de\s*60(?!\d)/
    ];

    return padroes.some(p => p.test(texto));
}

/**
 * Calcula o valor da inscrição aplicando 50% de desconto se a faixa etária for 60+.
 * Arredondamento monetário HALF_UP para 2 casas decimais.
 *
 * @param {number|string} valorBase
 * @param {string} faixaEtaria
 * @returns {number}
 */
export function calcularValorComDesconto60Mais(valorBase, faixaEtaria) {
    const valor = parseFloat(valorBase || 0);
    if (isNaN(valor) || valor <= 0) return 0;
    if (isFaixaEtaria60Mais(faixaEtaria)) {
        const centavos = Math.round(valor * 100);
        return Math.round(centavos * 0.5) / 100;
    }
    return Math.round(valor * 100) / 100;
}

/**
 * Remove caracteres não numéricos de um CPF ou documento.
 * @param {string} value 
 * @returns {string}
 */
export function unformatCPF(value) {
    if (!value) return '';
    return String(value).replace(/\D/g, '');
}

/**
 * Formata um CPF com a máscara 000.000.000-00.
 * @param {string} value 
 * @returns {string}
 */
export function formatCPF(value) {
    const clean = unformatCPF(value).substring(0, 11);
    if (!clean) return '';
    if (clean.length <= 3) return clean;
    if (clean.length <= 6) return `${clean.substring(0, 3)}.${clean.substring(3)}`;
    if (clean.length <= 9) return `${clean.substring(0, 3)}.${clean.substring(3, 6)}.${clean.substring(6)}`;
    return `${clean.substring(0, 3)}.${clean.substring(3, 6)}.${clean.substring(6, 9)}-${clean.substring(9, 11)}`;
}

/**
 * Valida os 11 dígitos e os dígitos verificadores de um CPF brasileiro.
 * @param {string} cpf 
 * @returns {boolean}
 */
export function isValidCPF(cpf) {
    const clean = unformatCPF(cpf);
    if (clean.length !== 11) return false;

    // Rejeita CPFs com todos os dígitos iguais (ex: 00000000000, 11111111111)
    if (/^(\d)\1{10}$/.test(clean)) return false;

    let soma = 0;
    for (let i = 0; i < 9; i++) {
        soma += parseInt(clean.charAt(i), 10) * (10 - i);
    }
    let resto = 11 - (soma % 11);
    let digito1 = resto === 10 || resto === 11 ? 0 : resto;
    if (digito1 !== parseInt(clean.charAt(9), 10)) return false;

    soma = 0;
    for (let i = 0; i < 10; i++) {
        soma += parseInt(clean.charAt(i), 10) * (11 - i);
    }
    resto = 11 - (soma % 11);
    let digito2 = resto === 10 || resto === 11 ? 0 : resto;
    return digito2 === parseInt(clean.charAt(10), 10);
}

/**
 * Formata um telefone celular ou fixo com DDD.
 * @param {string} value 
 * @returns {string}
 */
export function formatPhone(value) {
    if (!value) return '';
    const clean = String(value).replace(/\D/g, '').substring(0, 11);
    if (clean.length <= 2) return clean ? `(${clean}` : '';
    if (clean.length <= 6) return `(${clean.substring(0, 2)}) ${clean.substring(2)}`;
    if (clean.length <= 10) {
        return `(${clean.substring(0, 2)}) ${clean.substring(2, 6)}-${clean.substring(6)}`;
    }
    return `(${clean.substring(0, 2)}) ${clean.substring(2, 7)}-${clean.substring(7, 11)}`;
}

/**
 * Calcula o subtotal e o detalhamento por participante para exibição em tempo real na UI.
 * 
 * @param {Array<Object>} participantes Lista de participantes (titular e/ou terceiros)
 * @param {number|string} precoBaseLote Preço base do lote ativo
 * @param {Array<Object>} categorias Lista de categorias da corrida
 * @param {boolean} isGratuita Se a corrida é gratuita
 * @param {boolean} hasVoucher Se há voucher aplicado
 * @returns {Object} { detalhamento: Array, valorTotal: number, totalParticipantes: number }
 */
export function calcularSubtotalParticipantes(participantes, precoBaseLote, categorias = [], isGratuita = false, hasVoucher = false) {
    if (!Array.isArray(participantes) || participantes.length === 0) {
        return {
            detalhamento: [],
            valorTotal: 0,
            totalParticipantes: 0
        };
    }

    const precoBase = (isGratuita || hasVoucher) ? 0 : parseFloat(precoBaseLote || 0);

    const detalhamento = participantes.map((p, idx) => {
        const catId = p.id_categoria_corrida || p.categoria?.id || (typeof p.categoria === 'number' ? p.categoria : null);
        const categoriaObj = categorias.find(c => String(c.id) === String(catId)) || p.categoria || null;
        const faixa = categoriaObj?.faixa_etaria;
        const is60Mais = Boolean(!isGratuita && !hasVoucher && faixa && isFaixaEtaria60Mais(faixa));
        const valorFinal = (isGratuita || hasVoucher) ? 0 : (is60Mais ? calcularValorComDesconto60Mais(precoBase, faixa) : precoBase);

        return {
            ...p,
            index: idx,
            categoriaObj,
            is60Mais,
            valorOriginal: (isGratuita || hasVoucher) ? 0 : precoBase,
            valorFinal
        };
    });

    const valorTotal = detalhamento.reduce((acc, curr) => acc + (curr.valorFinal || 0), 0);

    return {
        detalhamento,
        valorTotal: Math.round(valorTotal * 100) / 100,
        totalParticipantes: participantes.length
    };
}


